import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import {
  Close,
  Receipt,
  Schedule,
  TrendingUp,
  ExpandMore,
  AccountBalance,
  Payments,
  OpenInNew,
  SentimentSatisfiedAlt,
  SentimentNeutral,
  SentimentVeryDissatisfied,
  ChevronLeft,
  ChevronRight,
  RestartAlt,
} from '@mui/icons-material'

interface RecognizedRevenueItem {
  org_name: string
  org_uuid: string
  type: string
  amount: number
  total_charge_amount: number
  frequency: string
  payment_type: string
  tier: number
  charge_date: string
  is_allocated: boolean
  description: string
  account_code?: string
  revenue_type?: string
  org_activity_status?: 'active' | 'dormant' | 'inactive'
  org_last_activity?: string | null
  discount_code?: string | null
  discount_original_amount?: number | null
  discount_amount?: number | null
  discount_final_amount?: number | null
  discount_expires_at?: string | null
  is_first_payment_month?: boolean
  charge_type?: 'Charge' | 'CreditNote' | string | null
}

interface SubscriptionRevenueOrgItem extends RecognizedRevenueItem {
  charge_count: number
  credit_note_count: number
  line_item_count: number
  charge_dates: string[]
  payment_types: string[]
  raw_items: RecognizedRevenueItem[]
}

interface ForecastInvoice {
  org_name: string
  org_uuid: string
  template_name: string
  amount: number
  schedule_unit: string
  schedule_period: number
  due_date: string
  contact_id: string
  repeating_invoice_id: string
  org_activity_status?: 'active' | 'dormant' | 'inactive'
  org_last_activity?: string | null
  discount_code?: string | null
  discount_original_amount?: number | null
  discount_amount?: number | null
  discount_final_amount?: number | null
  discount_expires_at?: string | null
}

interface RevenueBreakdownData {
  month: string
  recognized_revenue: RecognizedRevenueItem[]
  recognized_revenue_by_code?: {
    revenue_200: RecognizedRevenueItem[]
    revenue_data?: RecognizedRevenueItem[]
    revenue_other: RecognizedRevenueItem[]
    total_200: number
    total_data?: number
    total_other: number
  }
  forecast_revenue: ForecastInvoice[]
  forecast_risk_baseline?: {
    all: {
      medium_risk_percentage: number
      high_risk_percentage: number
    }
    by_schedule: Record<
      string,
      {
        medium_risk_percentage: number
        high_risk_percentage: number
      }
    >
  }
  subscription_new_org_baseline?: {
    new_org_percentage: number
  }
  arr_movements?: {
    lost_arr: number
    lost_org_count: number
  } | null
  total_recognized: number
  total_forecast: number
}

interface RevenueBreakdownModalProps {
  open: boolean
  onClose: () => void
  onMonthChange?: (month: string) => void
  month: string
  data: RevenueBreakdownData | null
  loading: boolean
  error?: string
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`revenue-tabpanel-${index}`}
      aria-labelledby={`revenue-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

const RevenueBreakdownModal: React.FC<RevenueBreakdownModalProps> = ({
  open,
  onClose,
  onMonthChange,
  month,
  data,
  loading,
  error,
}) => {
  const [currentTab, setCurrentTab] = React.useState(0)
  const [scheduleFilter, setScheduleFilter] = React.useState('all')
  const [riskFilter, setRiskFilter] = React.useState('all')
  const [discountFilter, setDiscountFilter] = React.useState('all')
  const [sourceFilter, setSourceFilter] = React.useState('all')
  const [newOrgFilter, setNewOrgFilter] = React.useState('all')
  const [arrDetailsExpanded, setArrDetailsExpanded] = React.useState(false)
  const hasInitializedOpenState = React.useRef(false)

  React.useEffect(() => {
    if (!open) {
      hasInitializedOpenState.current = false
      return
    }

    if (data && !hasInitializedOpenState.current) {
      const recognizedCount = data?.recognized_revenue?.length || 0
      const forecastCount = data?.forecast_revenue?.length || 0
      const hasCodeBreakdownOnOpen = Boolean(data?.recognized_revenue_by_code)
      const hasDataRevenueOnOpen =
        (data?.recognized_revenue_by_code?.revenue_data || []).length > 0
      const openForecastTabIndex = hasCodeBreakdownOnOpen
        ? hasDataRevenueOnOpen
          ? 3
          : 2
        : 1

      setCurrentTab(
        recognizedCount === 0 && forecastCount > 0 ? openForecastTabIndex : 0
      )
      setScheduleFilter('all')
      setRiskFilter('all')
      setDiscountFilter('all')
      setSourceFilter('all')
      setNewOrgFilter('all')
      hasInitializedOpenState.current = true
    }
  }, [open, data])

  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '$0'
    }
    return `$${Math.round(amount).toLocaleString()}`
  }

  const formatForecastCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '$0'
    }
    return `$${Math.round(amount).toLocaleString()}`
  }

  const normalizeScheduleUnit = (unit: string) => {
    const normalized = unit.toUpperCase()
    if (normalized === 'MONTHLY') return 'MONTH'
    if (normalized === 'WEEKLY') return 'WEEK'
    if (normalized === 'YEARLY') return 'YEAR'
    return normalized
  }

  const formatSchedule = (unit: string, period: number) => {
    const normalizedUnit = normalizeScheduleUnit(unit).toLowerCase()
    if (period === 1) {
      if (normalizedUnit === 'month') return 'monthly'
      if (normalizedUnit === 'week') return 'weekly'
      if (normalizedUnit === 'year') return 'yearly'
      return normalizedUnit
    }
    return `Every ${period} ${normalizedUnit}s`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatDueDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  const getMonthName = (monthStr: string) => {
    try {
      const [year, month] = monthStr.split('-')
      const date = new Date(parseInt(year), parseInt(month) - 1)
      return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      })
    } catch {
      return monthStr
    }
  }

  const shiftMonth = (monthStr: string, delta: number) => {
    const [year, monthNumber] = monthStr.split('-').map(Number)
    const date = new Date(year, monthNumber - 1 + delta)
    const shiftedYear = date.getFullYear()
    const shiftedMonth = String(date.getMonth() + 1).padStart(2, '0')
    return `${shiftedYear}-${shiftedMonth}`
  }

  const handlePreviousMonth = () => {
    if (onMonthChange && month) {
      onMonthChange(shiftMonth(month, -1))
    }
  }

  const handleNextMonth = () => {
    if (onMonthChange && month) {
      onMonthChange(shiftMonth(month, 1))
    }
  }

  const resetFilters = () => {
    setScheduleFilter('all')
    setRiskFilter('all')
    setDiscountFilter('all')
    setSourceFilter('all')
    setNewOrgFilter('all')
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
    resetFilters()
  }

  const getScheduleLabel = (invoice: ForecastInvoice) =>
    formatSchedule(invoice.schedule_unit, invoice.schedule_period)

  const getScheduleKey = (invoice: ForecastInvoice) =>
    `${normalizeScheduleUnit(invoice.schedule_unit)}:${invoice.schedule_period}`

  const getRecognizedScheduleLabel = (item: RecognizedRevenueItem) =>
    item.frequency || 'Unknown'

  const getRecognizedScheduleKey = (item: RecognizedRevenueItem) =>
    (item.frequency || 'Unknown').toLowerCase()

  const getAnnualizationMultiplier = (frequency?: string) => {
    const normalized = (frequency || 'Monthly').toLowerCase()
    if (normalized.includes('week')) return 52
    if (normalized.includes('quarter')) return 4
    if (normalized.includes('year') || normalized.includes('annual')) return 1
    return 12
  }

  const annualizeSubscriptionAmount = (
    amount: number | undefined | null,
    item: RecognizedRevenueItem
  ) => Math.max(0, amount || 0) * getAnnualizationMultiplier(item.frequency)

  const isLowRisk = (invoice: ForecastInvoice) =>
    invoice.org_activity_status === 'active'

  const isMediumRisk = (invoice: ForecastInvoice) =>
    invoice.org_activity_status === 'dormant'

  const isHighRisk = (invoice: ForecastInvoice) =>
    invoice.org_activity_status === 'inactive'

  const hasDiscount = (invoice: ForecastInvoice) => Boolean(invoice.discount_code)

  const hasDiscountExpiringInMonth = (item: { discount_expires_at?: string | null }) => {
    if (!item.discount_expires_at) {
      return false
    }
    const [year, monthNumber] = month.split('-').map(Number)
    const expiresAt = new Date(item.discount_expires_at)
    return (
      expiresAt.getFullYear() === year &&
      expiresAt.getMonth() === monthNumber - 1
    )
  }

  const getForecastSource = (invoice: ForecastInvoice) => {
    if (
      invoice.template_name?.toLowerCase().includes('stripe') ||
      invoice.repeating_invoice_id?.startsWith('sub_')
    ) {
      return 'Stripe'
    }
    return 'Xero'
  }

  const getRecognizedSource = (item: RecognizedRevenueItem) => {
    if (item.payment_type === 'Stripe') {
      return 'stripe'
    }
    if (['Invoice', 'Xero Invoice'].includes(item.payment_type)) {
      return 'xero'
    }
    return item.payment_type.toLowerCase()
  }

  const recognizedItemMatchesSource = (
    item: RecognizedRevenueItem,
    source: string
  ) => {
    if (source === 'all') {
      return true
    }
    const rawItems = (item as SubscriptionRevenueOrgItem).raw_items
    if (rawItems?.length) {
      return rawItems.some((rawItem) => getRecognizedSource(rawItem) === source)
    }
    return getRecognizedSource(item) === source
  }

  const groupSubscriptionRevenueByOrg = (
    items: RecognizedRevenueItem[]
  ): SubscriptionRevenueOrgItem[] => {
    const grouped = new Map<string, SubscriptionRevenueOrgItem>()

    items.forEach((item, index) => {
      const key = item.org_uuid || `${item.org_name}-${index}`
      const existing = grouped.get(key)
      const paymentType = item.payment_type || 'Unknown'
      const chargeType = item.charge_type || (item.amount < 0 ? 'CreditNote' : 'Charge')

      if (!existing) {
        grouped.set(key, {
          ...item,
          amount: item.amount,
          total_charge_amount: item.total_charge_amount || item.amount,
          payment_type: paymentType,
          charge_count: chargeType === 'CreditNote' ? 0 : 1,
          credit_note_count: chargeType === 'CreditNote' ? 1 : 0,
          line_item_count: 1,
          charge_dates: item.charge_date ? [item.charge_date] : [],
          payment_types: [paymentType],
          raw_items: [item],
        })
        return
      }

      existing.amount += item.amount
      existing.total_charge_amount += item.total_charge_amount || item.amount
      existing.charge_count += chargeType === 'CreditNote' ? 0 : 1
      existing.credit_note_count += chargeType === 'CreditNote' ? 1 : 0
      existing.line_item_count += 1
      existing.raw_items.push(item)
      if (item.charge_date) {
        existing.charge_dates.push(item.charge_date)
        existing.charge_date = existing.charge_dates.sort()[0]
      }
      if (!existing.payment_types.includes(paymentType)) {
        existing.payment_types.push(paymentType)
        existing.payment_type =
          existing.payment_types.length === 1 ? paymentType : 'Mixed'
      }
      existing.is_first_payment_month =
        existing.is_first_payment_month || item.is_first_payment_month
      if (!existing.discount_code && item.discount_code) {
        existing.discount_code = item.discount_code
        existing.discount_original_amount = item.discount_original_amount
        existing.discount_amount = item.discount_amount
        existing.discount_final_amount = item.discount_final_amount
        existing.discount_expires_at = item.discount_expires_at
      }
    })

    return Array.from(grouped.values()).sort((a, b) => {
      const dateCompare = (a.charge_date || '').localeCompare(b.charge_date || '')
      if (dateCompare !== 0) {
        return dateCompare
      }
      return b.amount - a.amount
    })
  }

  const renderForecastSource = (invoice: ForecastInvoice) => {
    const source = getForecastSource(invoice)
    return (
      <Chip
        label={source}
        size="small"
        variant="outlined"
        color={source === 'Stripe' ? 'info' : 'primary'}
        sx={{ minWidth: 64 }}
      />
    )
  }

  const renderActivityStatus = (status?: string) => {
    const normalizedStatus = status || 'inactive'
    const config = {
      active: {
        label: 'Active',
        color: 'success' as const,
        icon: <SentimentSatisfiedAlt fontSize="small" />,
      },
      dormant: {
        label: 'Dormant',
        color: 'warning' as const,
        icon: <SentimentNeutral fontSize="small" />,
      },
      inactive: {
        label: 'Inactive',
        color: 'error' as const,
        icon: <SentimentVeryDissatisfied fontSize="small" />,
      },
    }[normalizedStatus] || {
      label: 'Inactive',
      color: 'error' as const,
      icon: <SentimentVeryDissatisfied fontSize="small" />,
    }

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    )
  }

  const renderDiscountIndicator = (
    item: Pick<
      ForecastInvoice,
      | 'discount_code'
      | 'discount_original_amount'
      | 'discount_amount'
      | 'discount_final_amount'
      | 'discount_expires_at'
    >
  ) => {
    if (!item.discount_code) {
      return null
    }

    const discountPercent =
      item.discount_original_amount && item.discount_amount
        ? (item.discount_amount / item.discount_original_amount) * 100
        : null
    const discountText = [
      item.discount_amount ? `-${formatCurrency(item.discount_amount)}` : null,
      discountPercent !== null ? `${discountPercent.toFixed(1)}%` : null,
    ]
      .filter(Boolean)
      .join(' / ')
    const expiresAt = item.discount_expires_at
      ? new Date(item.discount_expires_at)
      : null
    const expiryText = expiresAt
      ? `, expires ${expiresAt.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}`
      : ''

    return (
      <Chip
        label={`${item.discount_code}${
          discountText ? `: ${discountText}` : ''
        }${expiryText}`}
        color="warning"
        size="small"
        variant="outlined"
      />
    )
  }

  const renderSubscriptionLineSummary = (item: SubscriptionRevenueOrgItem) => {
    const parts = [
      `${item.charge_count} charge${item.charge_count !== 1 ? 's' : ''}`,
      item.credit_note_count
        ? `${item.credit_note_count} credit note${
            item.credit_note_count !== 1 ? 's' : ''
          }`
        : null,
    ].filter(Boolean)

    return `${item.description} · Net of ${parts.join(' + ')}`
  }

  const createOrgLink = (orgName: string, orgUuid: string) => {
    if (!orgUuid || orgName === 'Unknown Organisation') {
      return (
        <Typography variant="body2" fontWeight="medium">
          {orgName}
        </Typography>
      )
    }

    return (
      <Link
        href={`/orgs/${orgUuid}?tab=1`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          textDecoration: 'none',
          fontWeight: 'medium',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {orgName}
        <OpenInNew fontSize="small" />
      </Link>
    )
  }

  const recognizedItems = data?.recognized_revenue || []
  const forecastItems = data?.forecast_revenue || []
  const dataRevenueItems = data?.recognized_revenue_by_code?.revenue_data || []
  const otherRevenueItems =
    data?.recognized_revenue_by_code?.revenue_other || []
  const hasCodeBreakdown = Boolean(data?.recognized_revenue_by_code)
  const hasDataRevenue = dataRevenueItems.length > 0
  const dataTabIndex = 1
  const otherTabIndex = hasDataRevenue ? 2 : 1
  const forecastTabIndex = hasCodeBreakdown ? (hasDataRevenue ? 3 : 2) : 1
  const combinedTabIndex = forecastTabIndex + 1

  React.useEffect(() => {
    if (currentTab > combinedTabIndex) {
      setCurrentTab(combinedTabIndex)
    }
  }, [combinedTabIndex, currentTab])

  // Additional safety checks for data integrity
  const safeRecognizedItems = recognizedItems.filter(
    (item) => item && typeof item.amount === 'number' && !isNaN(item.amount)
  )
  const safeForecastItems = forecastItems.filter(
    (item) => item && typeof item.amount === 'number' && !isNaN(item.amount)
  )
  const subscriptionRevenueItems = hasCodeBreakdown
    ? data?.recognized_revenue_by_code?.revenue_200 || []
    : safeRecognizedItems
  const safeSubscriptionRevenueItems = subscriptionRevenueItems.filter(
    (item) => item && typeof item.amount === 'number' && !isNaN(item.amount)
  )
  const groupedSubscriptionRevenueItems = React.useMemo(
    () => groupSubscriptionRevenueByOrg(safeSubscriptionRevenueItems),
    [safeSubscriptionRevenueItems]
  )
  const newSubscriptionOrgUuids = new Set(
    groupedSubscriptionRevenueItems
      .filter((item) => item.is_first_payment_month && item.org_uuid)
      .map((item) => item.org_uuid)
  )
  const isNewSubscriptionItem = (item: RecognizedRevenueItem) =>
    Boolean(item.is_first_payment_month) ||
    Boolean(item.org_uuid && newSubscriptionOrgUuids.has(item.org_uuid))
  const combinedRecognizedItems = hasCodeBreakdown
    ? [
        ...safeSubscriptionRevenueItems,
        ...dataRevenueItems,
        ...otherRevenueItems,
      ]
    : safeRecognizedItems
  const forecastScheduleOptions = React.useMemo(() => {
    const options = new Map<string, string>()
    safeForecastItems.forEach((item) => {
      options.set(getScheduleKey(item), getScheduleLabel(item))
    })
    return Array.from(options.entries()).map(([value, label]) => ({
      value,
      label,
    }))
  }, [safeForecastItems])
  const subscriptionScheduleOptions = React.useMemo(() => {
    const options = new Map<string, string>()
    groupedSubscriptionRevenueItems.forEach((item) => {
      options.set(getRecognizedScheduleKey(item), getRecognizedScheduleLabel(item))
    })
    return Array.from(options.entries()).map(([value, label]) => ({
      value,
      label,
    }))
  }, [groupedSubscriptionRevenueItems])
  const activeScheduleOptions =
    currentTab === 0 ? subscriptionScheduleOptions : forecastScheduleOptions
  const scheduleFilteredSubscriptionItems = groupedSubscriptionRevenueItems.filter(
    (item) =>
      scheduleFilter === 'all' || getRecognizedScheduleKey(item) === scheduleFilter
  )
  const riskFilteredSubscriptionItems = scheduleFilteredSubscriptionItems.filter(
    (item) => {
      if (riskFilter === 'low') {
        return item.org_activity_status === 'active'
      }
      if (riskFilter === 'medium') {
        return item.org_activity_status === 'dormant'
      }
      if (riskFilter === 'high') {
        return item.org_activity_status === 'inactive'
      }
      return true
    }
  )
  const filteredSubscriptionItems = riskFilteredSubscriptionItems.filter((item) => {
    if (newOrgFilter === 'new') {
      if (!isNewSubscriptionItem(item)) {
        return false
      }
    }
    if (newOrgFilter === 'existing') {
      if (isNewSubscriptionItem(item)) {
        return false
      }
    }
    if (sourceFilter !== 'all') {
      if (!recognizedItemMatchesSource(item, sourceFilter)) {
        return false
      }
    }
    if (discountFilter === 'discount') {
      if (!item.discount_code) {
        return false
      }
    }
    if (discountFilter === 'expiring') {
      if (!hasDiscountExpiringInMonth(item)) {
        return false
      }
    }
    if (discountFilter === 'none') {
      if (item.discount_code) {
        return false
      }
    }
    return true
  })
  const filteredSubscriptionTotal = filteredSubscriptionItems.reduce(
    (sum, item) => sum + item.amount,
    0
  )
  const newSubscriptionItems = filteredSubscriptionItems.filter(
    isNewSubscriptionItem
  )
  const newSubscriptionTotal = newSubscriptionItems.reduce(
    (sum, item) => sum + item.amount,
    0
  )
  const newSubscriptionOrgCount = new Set(
    newSubscriptionItems.map((item) => item.org_uuid).filter(Boolean)
  ).size
  const newSubscriptionPercent = filteredSubscriptionTotal
    ? (newSubscriptionTotal / filteredSubscriptionTotal) * 100
    : 0
  const newDiscountedArr = newSubscriptionItems
    .filter((item) => item.discount_code)
    .reduce(
      (sum, item) =>
        sum +
        annualizeSubscriptionAmount(
          item.discount_original_amount ?? item.total_charge_amount ?? item.amount,
          item
        ),
      0
    )
  const newPureArr = newSubscriptionItems
    .filter((item) => !item.discount_code)
    .reduce((sum, item) => sum + annualizeSubscriptionAmount(item.amount, item), 0)
  const newArrAdded = newDiscountedArr + newPureArr
  const lostArr = data?.arr_movements?.lost_arr || 0
  const lostArrOrgCount = data?.arr_movements?.lost_org_count || 0
  const netNewArr = newArrAdded - lostArr
  const scheduleFilteredForecastItems = safeForecastItems.filter(
    (item) =>
      scheduleFilter === 'all' || getScheduleKey(item) === scheduleFilter
  )
  const riskFilteredForecastItems = scheduleFilteredForecastItems.filter((item) => {
    if (riskFilter === 'low') {
      return isLowRisk(item)
    }
    if (riskFilter === 'medium') {
      return isMediumRisk(item)
    }
    if (riskFilter === 'high') {
      return isHighRisk(item)
    }
    return true
  })
  const filteredForecastItems = riskFilteredForecastItems.filter((item) => {
    if (sourceFilter !== 'all') {
      return getForecastSource(item).toLowerCase() === sourceFilter
    }
    if (discountFilter === 'discount') {
      return hasDiscount(item)
    }
    if (discountFilter === 'expiring') {
      return hasDiscountExpiringInMonth(item)
    }
    if (discountFilter === 'none') {
      return !hasDiscount(item)
    }
    return true
  })
  const filteredForecastTotal = filteredForecastItems.reduce(
    (sum, item) => sum + item.amount,
    0
  )
  const atRiskForecastTotal = filteredForecastItems
    .filter(isMediumRisk)
    .reduce((sum, item) => sum + item.amount, 0)
  const dormantForecastTotal = filteredForecastItems
    .filter(isHighRisk)
    .reduce((sum, item) => sum + item.amount, 0)
  const getPercentOfForecast = (amount: number) => {
    if (!filteredForecastTotal) {
      return 0
    }
    return (amount / filteredForecastTotal) * 100
  }
  const riskBaseline =
    scheduleFilter === 'all'
      ? data?.forecast_risk_baseline?.all
      : data?.forecast_risk_baseline?.by_schedule?.[scheduleFilter] ||
        data?.forecast_risk_baseline?.all
  const formatRiskDelta = (currentPercent: number, averagePercent = 0) => {
    const delta = currentPercent - averagePercent
    const sign = delta >= 0 ? '+' : ''
    return (
      <Typography
        component="span"
        variant="body2"
        color={delta > 0 ? 'error.main' : delta < 0 ? 'success.main' : 'textSecondary'}
      >
        {' '}
        ({sign}
        {delta.toFixed(1)}%)
      </Typography>
    )
  }
  const formatPositiveDelta = (currentPercent: number, averagePercent = 0) => {
    const delta = currentPercent - averagePercent
    const sign = delta >= 0 ? '+' : ''
    return (
      <Typography
        component="span"
        variant="body2"
        color={delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'textSecondary'}
      >
        {' '}
        ({sign}
        {delta.toFixed(1)}%)
      </Typography>
    )
  }
  const mediumRiskPercent = getPercentOfForecast(atRiskForecastTotal)
  const highRiskPercent = getPercentOfForecast(dormantForecastTotal)
  const recognizedTotalForHeader =
    filteredSubscriptionTotal +
    (data?.recognized_revenue_by_code?.total_data || 0) +
    (data?.recognized_revenue_by_code?.total_other || 0)
  const filteredTotalRevenue =
    recognizedTotalForHeader + Math.round(filteredForecastTotal)

  const renderXeroRevenueTable = (
    items: RecognizedRevenueItem[],
    label: string,
    chipColor: 'secondary' | 'info',
    amountColor: string
  ) => (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: 1, borderColor: 'divider' }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Organisation</strong>
            </TableCell>
            <TableCell>
              <strong>Revenue Type</strong>
            </TableCell>
            <TableCell>
              <strong>Payment Method</strong>
            </TableCell>
            <TableCell align="right">
              <strong>Amount</strong>
            </TableCell>
            <TableCell>
              <strong>Frequency</strong>
            </TableCell>
            <TableCell>
              <strong>Charge Date</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => (
            <TableRow key={index} hover>
              <TableCell>
                <Box>{createOrgLink(item.org_name, item.org_uuid)}</Box>
                <Typography variant="caption" color="textSecondary">
                  {item.description}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={label}
                  size="small"
                  color={chipColor}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={item.payment_type}
                  size="small"
                  variant="outlined"
                  color={
                    item.payment_type === 'Stripe' ? 'primary' : 'secondary'
                  }
                />
              </TableCell>
              <TableCell align="right">
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  color={amountColor}
                >
                  {formatCurrency(item.amount)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{item.frequency}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDate(item.charge_date)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Receipt color="primary" />
            <Button
              onClick={handlePreviousMonth}
              size="small"
              color="inherit"
              disabled={!onMonthChange || loading}
              aria-label="Previous month"
              title="Previous month"
              sx={{ minWidth: 32, px: 0.5 }}
            >
              <ChevronLeft />
            </Button>
            <Typography variant="h6">
              Revenue Breakdown - {getMonthName(month)}
            </Typography>
            <Button
              onClick={handleNextMonth}
              size="small"
              color="inherit"
              disabled={!onMonthChange || loading}
              aria-label="Next month"
              title="Next month"
              sx={{ minWidth: 32, px: 0.5 }}
            >
              <ChevronRight />
            </Button>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              label={`Total: ${formatCurrency(filteredTotalRevenue)}`}
              color="primary"
              variant="outlined"
            />
            <Button onClick={onClose} size="small" color="inherit">
              <Close />
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading revenue details...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : !data ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="textSecondary">
              No revenue data available for this month.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Box display="flex" gap={2} mb={3}>
              <Paper sx={{ p: 2, flex: 1, bgcolor: 'success.50' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <AccountBalance color="success" fontSize="small" />
                  <Typography variant="subtitle2" color="success.main">
                    Subscription Revenue
                  </Typography>
                </Box>
                <Typography variant="h5" color="success.main">
                  {formatCurrency(filteredSubscriptionTotal)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {filteredSubscriptionItems.length}{' '}
                  org
                  {filteredSubscriptionItems.length !== 1 ? 's' : ''}
                </Typography>
                <Typography variant="body2" color="success.main">
                  New orgs: {formatCurrency(newSubscriptionTotal)} (
                  {newSubscriptionPercent.toFixed(1)}%, {newSubscriptionOrgCount}{' '}
                  org
                  {newSubscriptionOrgCount !== 1 ? 's' : ''})
                  {formatPositiveDelta(
                    newSubscriptionPercent,
                    data.subscription_new_org_baseline?.new_org_percentage
                  )}
                </Typography>
                <Box
                  display="inline-flex"
                  alignItems="center"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setArrDetailsExpanded((expanded) => !expanded)}
                >
                  <Typography variant="body2" color="success.main">
                    Net new ARR: {formatCurrency(netNewArr)}
                  </Typography>
                  <ExpandMore
                    sx={{
                      ml: 0.5,
                      color: 'success.main',
                      transform: arrDetailsExpanded
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 150ms ease',
                    }}
                  />
                </Box>
                {arrDetailsExpanded && (
                  <Box mt={0.25}>
                    <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.25 }}>
                      New ARR added: {formatCurrency(newArrAdded)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.25 }}>
                      Discounted ARR: {formatCurrency(newDiscountedArr)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.25 }}>
                      Pure ARR: {formatCurrency(newPureArr)}
                    </Typography>
                    <Typography variant="body2" color="error.main" sx={{ lineHeight: 1.25 }}>
                      Lost ARR: {formatCurrency(lostArr)} ({lostArrOrgCount}{' '}
                      org
                      {lostArrOrgCount !== 1 ? 's' : ''})
                    </Typography>
                  </Box>
                )}
              </Paper>

              {hasDataRevenue && (
                <Paper sx={{ p: 2, flex: 1, bgcolor: 'secondary.50' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Payments color="secondary" fontSize="small" />
                    <Typography variant="subtitle2" color="secondary.main">
                      Data Revenue
                    </Typography>
                  </Box>
                  <Typography variant="h5" color="secondary.main">
                    {formatCurrency(
                      data.recognized_revenue_by_code?.total_data || 0
                    )}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {dataRevenueItems.length} charge
                    {dataRevenueItems.length !== 1 ? 's' : ''}
                  </Typography>
                </Paper>
              )}

              {data?.recognized_revenue_by_code?.revenue_other &&
                data.recognized_revenue_by_code.revenue_other.length > 0 && (
                  <Paper sx={{ p: 2, flex: 1, bgcolor: 'info.50' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Payments color="info" fontSize="small" />
                      <Typography variant="subtitle2" color="info.main">
                        Other Revenue
                      </Typography>
                    </Box>
                    <Typography variant="h5" color="info.main">
                      {formatCurrency(
                        data.recognized_revenue_by_code.total_other
                      )}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {data.recognized_revenue_by_code.revenue_other.length}{' '}
                      charge
                      {data.recognized_revenue_by_code.revenue_other.length !==
                      1
                        ? 's'
                        : ''}
                    </Typography>
                  </Paper>
                )}

              <Paper sx={{ p: 2, flex: 1, bgcolor: 'warning.50' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <TrendingUp color="warning" fontSize="small" />
                  <Typography variant="subtitle2" color="warning.main">
                    Forecast Revenue
                  </Typography>
                </Box>
                <Typography variant="h5" color="warning.main">
                  {formatForecastCurrency(filteredForecastTotal)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {filteredForecastItems.length} projected charge
                  {filteredForecastItems.length !== 1 ? 's' : ''}
                </Typography>
                {filteredForecastTotal > 0 && (
                  <>
                    <Typography variant="body2" color="secondary.main">
                      Medium risk: {formatForecastCurrency(atRiskForecastTotal)} (
                      {mediumRiskPercent.toFixed(1)}%)
                      {formatRiskDelta(
                        mediumRiskPercent,
                        riskBaseline?.medium_risk_percentage
                      )}
                    </Typography>
                    <Typography variant="body2" color="error.main">
                      High risk: {formatForecastCurrency(dormantForecastTotal)} (
                      {highRiskPercent.toFixed(1)}%)
                      {formatRiskDelta(
                        highRiskPercent,
                        riskBaseline?.high_risk_percentage
                      )}
                    </Typography>
                  </>
                )}
              </Paper>
            </Box>

              {((currentTab === 0 && groupedSubscriptionRevenueItems.length > 0) ||
              (currentTab === forecastTabIndex && safeForecastItems.length > 0)) && (
              <Box display="flex" justifyContent="flex-end" gap={2} mb={2}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="forecast-schedule-filter-label">
                    Schedule
                  </InputLabel>
                  <Select
                    labelId="forecast-schedule-filter-label"
                    value={scheduleFilter}
                    label="Schedule"
                    onChange={(event) => setScheduleFilter(event.target.value)}
                  >
                    <MenuItem value="all">All schedules</MenuItem>
                    {activeScheduleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="forecast-risk-filter-label">Risk</InputLabel>
                  <Select
                    labelId="forecast-risk-filter-label"
                    value={riskFilter}
                    label="Risk"
                    onChange={(event) => setRiskFilter(event.target.value)}
                  >
                    <MenuItem value="all">All risk levels</MenuItem>
                    <MenuItem value="low">Low risk</MenuItem>
                    <MenuItem value="medium">Medium risk</MenuItem>
                    <MenuItem value="high">High risk</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel id="forecast-discount-filter-label">
                    Discount
                  </InputLabel>
                  <Select
                    labelId="forecast-discount-filter-label"
                    value={discountFilter}
                    label="Discount"
                    onChange={(event) =>
                      setDiscountFilter(event.target.value)
                    }
                  >
                    <MenuItem value="all">All discounts</MenuItem>
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="discount">Discount</MenuItem>
                    <MenuItem value="expiring">Expiring</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="forecast-source-filter-label">
                    Source
                  </InputLabel>
                  <Select
                    labelId="forecast-source-filter-label"
                    value={sourceFilter}
                    label="Source"
                    onChange={(event) => setSourceFilter(event.target.value)}
                  >
                    <MenuItem value="all">All sources</MenuItem>
                    <MenuItem value="xero">Xero</MenuItem>
                    <MenuItem value="stripe">Stripe</MenuItem>
                  </Select>
                </FormControl>
                {currentTab === 0 && (
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="subscription-new-filter-label">
                      Org type
                    </InputLabel>
                    <Select
                      labelId="subscription-new-filter-label"
                      value={newOrgFilter}
                      label="Org type"
                      onChange={(event) => setNewOrgFilter(event.target.value)}
                    >
                      <MenuItem value="all">All orgs</MenuItem>
                      <MenuItem value="new">New</MenuItem>
                      <MenuItem value="existing">Existing</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAlt />}
                  onClick={resetFilters}
                  sx={{ alignSelf: 'center', minHeight: 40 }}
                >
                  Reset
                </Button>
              </Box>
            )}

            {/* Tabs for different revenue types */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={currentTab} onChange={handleTabChange}>
                <Tab
                  icon={<AccountBalance />}
                  iconPosition="start"
                  label={`Subscription Revenue (${filteredSubscriptionItems.length})`}
                  disabled={groupedSubscriptionRevenueItems.length === 0}
                />
                {hasCodeBreakdown && hasDataRevenue && (
                  <Tab
                    icon={<Payments />}
                    iconPosition="start"
                    label={`Data Revenue (${dataRevenueItems.length})`}
                  />
                )}
                {hasCodeBreakdown && (
                  <Tab
                    icon={<Payments />}
                    iconPosition="start"
                    label={`Other Revenue (${otherRevenueItems.length})`}
                    disabled={otherRevenueItems.length === 0}
                  />
                )}
                <Tab
                  icon={<TrendingUp />}
                  iconPosition="start"
                  label={`Forecast (${filteredForecastItems.length})`}
                  disabled={filteredForecastItems.length === 0}
                />
                <Tab
                  icon={<Receipt />}
                  iconPosition="start"
                  label="All Combined"
                />
              </Tabs>
            </Box>

            {/* Recognized Revenue Tab */}
            <TabPanel value={currentTab} index={0}>
              {filteredSubscriptionItems.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="textSecondary">
                    No recognized revenue for this month.
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: 1, borderColor: 'divider' }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Organisation</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Payment Method</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Current Activity</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Net Amount</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Frequency</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Activity Date</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSubscriptionItems.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {createOrgLink(item.org_name, item.org_uuid)}
                              {item.is_first_payment_month && (
                                <Chip
                                  label="New"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                              {renderSubscriptionLineSummary(item)}
                            </Typography>
                            {renderDiscountIndicator(item) && (
                              <Box mt={0.5}>
                                {renderDiscountIndicator(item)}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.payment_type}
                              size="small"
                              variant="outlined"
                              color={
                                item.payment_type === 'Stripe'
                                  ? 'primary'
                                  : 'secondary'
                              }
                            />
                          </TableCell>
                          <TableCell align="center">
                            {renderActivityStatus(item.org_activity_status)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color="primary"
                            >
                              {formatCurrency(item.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {item.frequency}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(item.charge_date)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>

            {/* Data Revenue Tab */}
            {hasCodeBreakdown && hasDataRevenue && (
              <TabPanel value={currentTab} index={dataTabIndex}>
                {renderXeroRevenueTable(
                  dataRevenueItems,
                  'Data Revenue',
                  'secondary',
                  'secondary.main'
                )}
              </TabPanel>
            )}

            {/* Other Revenue Tab */}
            {hasCodeBreakdown && (
              <TabPanel value={currentTab} index={otherTabIndex}>
                {otherRevenueItems.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body1" color="textSecondary">
                      No other revenue for this month.
                    </Typography>
                  </Box>
                ) : (
                  renderXeroRevenueTable(
                    otherRevenueItems,
                    'Other Revenue',
                    'info',
                    'info.main'
                  )
                )}
              </TabPanel>
            )}

            {/* Forecast Revenue Tab */}
            <TabPanel value={currentTab} index={forecastTabIndex}>
              {filteredForecastItems.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="textSecondary">
                    No forecast revenue for this month.
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: 1, borderColor: 'divider' }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>Organisation</strong>
                        </TableCell>
                        <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>
                          <strong>Due Date</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Activity</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Amount</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Schedule</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Source</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredForecastItems.map((invoice, index) => (
                        <TableRow
                          key={`${invoice.repeating_invoice_id}-${index}`}
                          hover
                        >
                          <TableCell>
                            <Box>
                              {createOrgLink(invoice.org_name, invoice.org_uuid)}
                            </Box>
                            {renderDiscountIndicator(invoice) && (
                              <Box mt={0.5}>
                                {renderDiscountIndicator(invoice)}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Schedule fontSize="small" color="action" />
                              <Typography variant="body2">
                                {formatDueDate(invoice.due_date)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {renderActivityStatus(invoice.org_activity_status)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color="warning.main"
                            >
                              {formatForecastCurrency(invoice.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={formatSchedule(
                                invoice.schedule_unit,
                                invoice.schedule_period
                              )}
                              size="small"
                              variant="outlined"
                              color="default"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {renderForecastSource(invoice)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>

            {/* Combined View Tab */}
            <TabPanel value={currentTab} index={combinedTabIndex}>
              <Box>
                {combinedRecognizedItems.length > 0 && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        width="100%"
                      >
                        <AccountBalance color="primary" />
                        <Typography variant="h6" color="primary">
                          Recognized Revenue
                        </Typography>
                        <Chip
                          label={formatCurrency(recognizedTotalForHeader)}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Organisation</TableCell>
                              <TableCell>Payment</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell>Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {combinedRecognizedItems.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  {createOrgLink(item.org_name, item.org_uuid)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={item.payment_type}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    color: 'primary.main',
                                    fontWeight: 'medium',
                                  }}
                                >
                                  {formatCurrency(item.amount)}
                                </TableCell>
                                <TableCell>
                                  {formatDate(item.charge_date)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}

                {filteredForecastItems.length > 0 && (
                  <Accordion
                    defaultExpanded={combinedRecognizedItems.length === 0}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        width="100%"
                      >
                        <TrendingUp color="warning" />
                        <Typography variant="h6" color="warning.main">
                          Forecast Revenue
                        </Typography>
                        <Chip
                          label={formatForecastCurrency(filteredForecastTotal)}
                          color="warning"
                          size="small"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Organisation</TableCell>
                              <TableCell>Schedule</TableCell>
                              <TableCell align="center">Activity</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>
                                Due Date
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredForecastItems.map((item, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <Box>
                                    {createOrgLink(item.org_name, item.org_uuid)}
                                  </Box>
                                  {renderDiscountIndicator(item) && (
                                    <Box mt={0.5}>
                                      {renderDiscountIndicator(item)}
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={formatSchedule(
                                      item.schedule_unit,
                                      item.schedule_period
                                    )}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {renderActivityStatus(
                                    item.org_activity_status
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    color: 'warning.main',
                                    fontWeight: 'medium',
                                  }}
                                >
                                  {formatForecastCurrency(item.amount)}
                                </TableCell>
                                <TableCell sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>
                                  {formatDueDate(item.due_date)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}

                {combinedRecognizedItems.length === 0 &&
                  filteredForecastItems.length === 0 && (
                    <Box textAlign="center" py={4}>
                      <Typography variant="body1" color="textSecondary">
                        No revenue data available for this month.
                      </Typography>
                    </Box>
                  )}
              </Box>
            </TabPanel>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Note:</strong> All revenue is recognized immediately
                when charges are made, regardless of billing frequency. Forecast
                revenue is based on active Xero recurring invoice templates and
                Stripe subscription projections. Forecasts may vary due to
                template changes, cancellations, or manual adjustments.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RevenueBreakdownModal
